import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppActionMenu from '../../../../components/AppActionMenu';

export default function HolidayMasterTable({
  records = [],
  isLoading = false,
  onArchive,
  onDelete,
  onResetFilters,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const getEntryPath = (id) => {
    const isPayroll = location.pathname.startsWith('/payroll');
    const base = isPayroll ? '/payroll/holiday-master' : '/masters/holiday-master';
    return `${base}/entry/${id}`;
  };

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <p className="text-xs font-semibold text-on-surface-variant">Loading Holiday Master...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <span className="material-symbols-outlined text-3xl">event_busy</span>
          </div>
          <h3 className="text-lg font-bold text-on-surface">No entries meet selected criteria</h3>
          <p className="text-xs text-on-surface-variant max-w-md">
            Either there are no entries in the selected module or you have selected invalid filter conditions.
          </p>
          {onResetFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="mt-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[750px] text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
              <th className="px-5 py-3.5">Name</th>
              <th className="px-5 py-3.5">Date</th>
              <th className="px-5 py-3.5">Group</th>
              <th className="px-5 py-3.5 text-center">Status</th>
              <th className="w-20 px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((item) => {
              const isActive = item.status === 'Active';
              return (
                <tr
                  key={item.id}
                  className="transition-colors hover:bg-surface-container-low/50 group cursor-pointer"
                  onClick={() => navigate(getEntryPath(item.id))}
                >
                  {/* Name */}
                  <td className="px-5 py-3.5 font-bold text-on-surface group-hover:text-primary transition-colors">
                    <div className="flex items-center gap-2.5">
                      <span className="material-symbols-outlined text-base text-primary/70">event</span>
                      <span>{item.name}</span>
                    </div>
                  </td>

                  {/* Date */}
                  <td className="px-5 py-3.5 text-xs font-medium text-on-surface-variant">
                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-surface-container-low px-2.5 py-1 text-xs font-semibold text-on-surface border border-outline-variant/20">
                      <span className="material-symbols-outlined text-xs text-primary">calendar_today</span>
                      <span>{item.dateDisplay || item.date}</span>
                    </span>
                  </td>

                  {/* Group */}
                  <td className="px-5 py-3.5 text-xs text-on-surface-variant">
                    {item.groups && item.groups.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {item.groups.map((grp) => (
                          <span
                            key={grp}
                            className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-bold text-primary border border-primary/20"
                          >
                            {grp}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-outline italic">All Groups</span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-5 py-3.5 text-center">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                        isActive
                          ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20'
                          : 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
                      }`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                      {item.status}
                    </span>
                  </td>

                  {/* Actions */}
                  <td
                    className="px-5 py-3.5 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <AppActionMenu
                      triggerLabel=""
                      triggerIcon="more_vert"
                      showChevron={false}
                      triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                      items={[
                        { key: 'edit', label: 'Edit', icon: 'edit' },
                        {
                          key: 'archive',
                          label: isActive ? 'Archive' : 'Activate',
                          icon: isActive ? 'archive' : 'unarchive',
                        },
                        { key: 'delete', label: 'Delete', icon: 'delete' },
                      ]}
                      onSelect={(key) => {
                        if (key === 'edit') navigate(getEntryPath(item.id));
                        if (key === 'archive') onArchive?.(item);
                        if (key === 'delete') onDelete?.(item.id, item.name);
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
